import { BASE_PATHS, WIDGET_LOADER_PATH, pickApiUrl, resolveDeployment, type DeployEnv, type Deployment } from "./deployment.js";

/** Amendment C: the product name lives in exactly this one constant on the frontend. */
export const PRODUCT_NAME = "Sagot";
export const COMPANY_NAME = "CRUD.IT Solutions";
export const PRODUCT_BYLINE = "by CRUD.IT";

/**
 * Deployment (DEC-001, DEC-038, DEC-040): `VITE_ENV` (production | staging; inferred from
 * VITE_ROOT_DOMAIN when unset) + `VITE_ROOT_DOMAIN` resolve the hosts through `./deployment.js`.
 * DEC-040: one frontend origin `app.<root>` — console `/`, admin `/admin/`, web app `/chat/<slug>/`,
 * widget `/widget.js`; API on `api.<root>`. No `admin.` / `cdn.` / wildcard tenant hosts.
 */
export const DEPLOYMENT: Deployment = resolveDeployment({ env: import.meta.env.VITE_ENV, rootDomain: import.meta.env.VITE_ROOT_DOMAIN });
export const DEPLOY_ENV: DeployEnv = DEPLOYMENT.env;
/** Platform root (`sagot.cruditsolutions.com`); marketing and docs live here in every environment. */
export const PLATFORM_ROOT_DOMAIN: string = DEPLOYMENT.platformRoot;

/** `app.` / `api.` on production, `staging-app.` / `staging-api.` on staging. */
export const HOSTS: Deployment["hosts"] = DEPLOYMENT.hosts;

/** API origin: `VITE_API_URL` (deprecated fallback `VITE_API_BASE_URL`); unset means callers use their dev default. */
export const API_URL: string | undefined = pickApiUrl({ VITE_API_URL: import.meta.env.VITE_API_URL, VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL });
/** Canonical API origin shown to tenants (curl samples) and used by the widget loader fallback. */
export const API_ORIGIN: string = API_URL && /^https?:\/\//.test(API_URL) ? new URL(API_URL).origin : `https://${HOSTS.api}`;

/** Canonical single frontend origin (`VITE_PLATFORM_URL` override, e.g. a local `pnpm preview:web`). */
const platformUrlEnv = import.meta.env.VITE_PLATFORM_URL?.trim() ?? "";
const PLATFORM_URL_OVERRIDE: string | undefined = platformUrlEnv === "" ? undefined : platformUrlEnv;
export const PLATFORM_ORIGIN: string = PLATFORM_URL_OVERRIDE ? new URL(PLATFORM_URL_OVERRIDE).origin : `https://${HOSTS.app}`;
/** Marketing site on the apex (DEC-044): same Vercel project as the app, second domain. */
export const MARKETING_ORIGIN = `https://${PLATFORM_ROOT_DOMAIN}`;
/** `app.sagot.cruditsolutions.com` (no scheme) for display. */
export const PLATFORM_HOST: string = new URL(PLATFORM_ORIGIN).host;

/** Local Vite dev servers (frontend/apps/<app>/package.json `--port`); each app keeps its base path. */
export const DEV_ORIGINS = {
  console: "http://localhost:5173",
  admin: "http://localhost:5174",
  webapp: "http://localhost:5175",
  widget: "http://localhost:5176",
} as const;
export type PlatformApp = keyof typeof DEV_ORIGINS;

/** Origin an app is served from: the platform origin, or its own Vite port in dev (unless overridden). */
export function appOrigin(app: PlatformApp): string {
  if (PLATFORM_URL_OVERRIDE || !import.meta.env.DEV) return PLATFORM_ORIGIN;
  return DEV_ORIGINS[app];
}

const withSlash = (path: string) => (path.startsWith("/") ? path : `/${path}`);
const ADMIN_PREFIX = BASE_PATHS.admin.slice(0, -1);
const WEBAPP_PREFIX = BASE_PATHS.webapp.slice(0, -1);

/** Absolute console URL (`/login`, `/w/kha/dashboard`). */
export function consoleUrl(path = "/"): string {
  return `${appOrigin("console")}${withSlash(path)}`;
}

/** Absolute admin URL: `adminUrl("/tenants")` → `https://app.<root>/admin/tenants`. */
export function adminUrl(path = "/"): string {
  return `${appOrigin("admin")}${ADMIN_PREFIX}${withSlash(path)}`;
}

/** Tenant web-app path `/chat/<slug>` (+ in-app path). */
export function webappPath(slug: string, path = "/"): string {
  const rest = withSlash(path);
  return `${WEBAPP_PREFIX}/${encodeURIComponent(slug)}${rest === "/" ? "" : rest}`;
}

/** Absolute tenant web-app URL: `webappUrl("kha")` → `https://app.<root>/chat/kha`. */
export function webappUrl(slug: string, path = "/"): string {
  return `${appOrigin("webapp")}${webappPath(slug, path)}`;
}

/** Canonical public link a tenant shares (always the platform origin, never a dev port). */
export function webappPublicUrl(slug: string): string {
  return `${PLATFORM_ORIGIN}${webappPath(slug)}`;
}

/** Query parameter that picks the agent on the tenant web app (`/chat/<slug>?agent=<agent_key>`). */
export const WEBAPP_AGENT_PARAM = "agent";

/** Public web-app link that opens the chat with one agent selected (what a visitor of that agent sees). */
export function webappAgentUrl(slug: string, agentKey: string): string {
  return `${webappPublicUrl(slug)}?${WEBAPP_AGENT_PARAM}=${encodeURIComponent(agentKey)}`;
}

/** `app.sagot.cruditsolutions.com/chat/<slug>` for display next to slug inputs. */
export function webappDisplayUrl(slug: string): string {
  return `${PLATFORM_HOST}${webappPath(slug)}`;
}

/** Widget loader URL for the embed snippet (DEC-011 updated by DEC-040). */
export function widgetSrc(): string {
  return `${PLATFORM_ORIGIN}${WIDGET_LOADER_PATH}`;
}

/** Public URLs (marketing and the developer docs, `<root>/docs`, on the root; app links via the helpers above). */
export const APP_URLS = {
  console: consoleUrl("/"),
  admin: adminUrl("/"),
  marketing: MARKETING_ORIGIN,
  docs: `${MARKETING_ORIGIN}/docs`,
  docsWidget: `${MARKETING_ORIGIN}/docs/widget`,
  docsApi: `${MARKETING_ORIGIN}/docs/api`,
} as const;

/** Developer REST API: path prefix on `API_ORIGIN` (mirrors backend `API_PREFIX`) and the per-key limit. */
export const DEVELOPER_API_PATH = "/api/v1";
export const DEVELOPER_API_RATE_LIMIT_PER_MINUTE = 120;

export const LEGAL_LINKS = {
  terms: `${APP_URLS.marketing}/terms`,
  privacy: `${APP_URLS.marketing}/privacy`,
  status: `${APP_URLS.marketing}/status`,
} as const;

/**
 * Tenant slugs that collide with system hosts or email DNS labels (mirrors backend
 * `app.core.constants.product.RESERVED_SLUGS`; the server is authoritative).
 */
export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  "admin",
  "chat",
  "widget",
  "v",
  "assets",
  "w",
  "api",
  "login",
  "register",
  "onboarding",
  "invite",
  "app",
  "files",
  "www",
  "staging",
  "status",
  "docs",
  "mail",
  "in",
  "send",
  "bounce",
]);

export function isReservedSlug(slug: string): boolean {
  return RESERVED_SLUGS.has(slug.trim().toLowerCase());
}

/** Default workspace timezone and currency (CLAUDE.md rule 15). */
export const DEFAULT_TIMEZONE = "Asia/Manila";
export const DEFAULT_CURRENCY = "PHP";
export const DEFAULT_LOCALE = "en-PH";

export { BASE_PATHS, RESERVED_CONSOLE_PREFIXES, WIDGET_ASSET_PREFIX, WIDGET_LOADER_PATH } from "./deployment.js";

/** `app.sagot.cruditsolutions.com/chat/` — leading addon of tenant slug inputs. */
export const WEBAPP_DISPLAY_PREFIX = `${PLATFORM_HOST}${BASE_PATHS.webapp}`;
